import { Trans, useLingui } from '@lingui/react/macro';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import { Alert, Button, Card, CardContent, LinearProgress, linearProgressClasses, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

interface NextStepsProgressProps {
  progress: number | undefined;
}

export const NextStepsProgress = ({ progress }: NextStepsProgressProps) => {
  const theme = useTheme();
  const { t } = useLingui();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
          <CheckBoxOutlinedIcon />
          <Typography>
            <Trans>Next Steps</Trans>
          </Typography>
        </Stack>
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            <Trans>
              Complete these steps at the earliest to complete your application process. These will change as you progress, so keep an eye out for new tasks.
            </Trans>
          </Typography>
        </Alert>
        <Stack direction="row" alignItems="center" gap={1} my={1.5}>
          <TaskOutlinedIcon />
          <Typography>
            <Trans>Reserve your Course Seat</Trans>
          </Typography>
        </Stack>
        <LinearProgress
          variant={progress === undefined ? 'indeterminate' : 'determinate'}
          value={progress}
          sx={{
            my: 2,
            height: 8,
            borderRadius: 5,
            [`&.${linearProgressClasses.colorPrimary}`]: {
              backgroundColor: theme.palette.menuColor,
            },
            [`& .${linearProgressClasses.bar}`]: {
              borderRadius: 5,
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        />
        <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" gap={isMobile ? 1 : 3}>
          <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? 1 : 3}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2" fontSize={theme.spacing(2)} fontWeight={700} color="#767676">
                <Trans>Percentage Complete:</Trans>
              </Typography>
              {progress === undefined ? (
                <CircularProgress size="20px" />
              ) : (
                <Typography fontSize={theme.spacing(2)} fontWeight={700} color="#00968F">{`${progress?.toFixed(0)}%`}</Typography>
              )}
            </Stack>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2" fontSize={theme.spacing(2)} fontWeight={700} color="#767676">
                <Trans>Estimated time to finish:</Trans>
              </Typography>
              {progress === undefined ? (
                <CircularProgress size="20px" />
              ) : (
                <Typography fontSize={theme.spacing(2)} fontWeight={700} color="#EF6C00">
                  {t`${((15 * (100 - progress)) / 100).toFixed(0)} minutes`}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Button variant="contained" href="/course-reservation" size="large">
            <Typography component="span">
              <Trans>Complete Your Reservation</Trans>
            </Typography>
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
